import React from 'react';
import { TableRow, Box, Grid2 } from '@mui/material';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import CustomTable from '@ui-components/Table/CustomTable';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTableCell from '@ui-components/Table/CustomTableCell';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';

import { CSS } from '@dnd-kit/utilities';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import DragIcon from '@assets/images/drag-icon.svg';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomToolTip from '@ui-components/ToolTip/CustomToolTip';
import PidIcon from '@assets/images/pid.svg';
import type { SessionFields } from '../../API/services/IntakeFormService';
import type { FormikErrors, FormikTouched } from 'formik';

interface SessionFieldsProps {
  sessionFields: SessionFields[];
  setSessionFields: React.Dispatch<React.SetStateAction<SessionFields[]>>;
  errors?: string | string[] | FormikErrors<SessionFields>[];
  touched?: boolean | string[] | FormikTouched<SessionFields>[];
}

const SortableRow: React.FC<{
  row: SessionFields;
  onChange: (id: number, key: keyof SessionFields, value: string) => void;
  errors?: string | FormikErrors<SessionFields>;
  touched?: string | FormikTouched<SessionFields>;
}> = ({ row, onChange, errors, touched }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: row.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow ref={setNodeRef} style={style} {...attributes}>
      <CustomTableCell
        id="drag-handle-cell"
        {...attributes}
        {...listeners}
        className="width-5"
      >
        <CustomIconButton
          id="drag-icon"
          size="small"
          icon={<DragIcon />}
        ></CustomIconButton>
      </CustomTableCell>
      <CustomTableCell id="field-name-cell">
        <Box className="flex-justify-space-between">
          <Box>
            <CustomTypography id="session-field-name-cell-text" variant="body3">
              {row.fieldName}
            </CustomTypography>
          </Box>
          <CustomToolTip
            id="tooltip-pid"
            title="PID Record"
            className="cursor-pointer"
          >
            <Box>{row.fieldName === 'Patient MRN' && <PidIcon />}</Box>
          </CustomToolTip>
        </Box>
      </CustomTableCell>
      <CustomTableCell id="field-label-cell">
        <CustomTextField
          id="field-label-cell-input"
          size="small"
          variant="outlined"
          value={row.fieldLabel}
          placeholder="Enter alias name for this field"
          onChange={(e) => onChange(row.id, 'fieldLabel', e.target.value)}
        />
      </CustomTableCell>
      <CustomTableCell id="field-label-cell">
        {row.fieldName.toLowerCase() === 'ed/ip' ||
        row.fieldName.toLowerCase() === 'cart' ? (
          <Box>
            <CustomTextField
              id="field-values-cell-input"
              size="small"
              fullWidth
              value={row.fieldValues}
              placeholder="Ex. Value 1, Value 2..."
              onChange={(e) => onChange(row.id, 'fieldValues', e.target.value)}
            />
            {typeof errors !== 'string' &&
              typeof touched !== 'string' &&
              errors?.fieldValues &&
              touched?.fieldValues && (
                <CustomTypography
                  id="validate-diagnosis"
                  color="error"
                  variant="body3"
                  className="pt-8"
                >
                  {errors.fieldValues}
                </CustomTypography>
              )}
          </Box>
        ) : (
          <CustomTypography
            id="no-data-cell-text"
            variant="body3"
            color="textDisabled"
          >
            No field values are allowed
          </CustomTypography>
        )}
      </CustomTableCell>
    </TableRow>
  );
};
const SessionFieldsComponent: React.FC<SessionFieldsProps> = ({
  sessionFields,
  setSessionFields,
  errors,
  touched,
}) => {
  const sensors = useSensors(useSensor(PointerSensor));
  console.log('sessionFields', sessionFields);
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sessionFields.findIndex((r) => r.id === active.id);
    const newIndex = sessionFields.findIndex((r) => r.id === over.id);
    setSessionFields((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const handleTextChange = (
    id: number,
    key: keyof SessionFields,
    value: string
  ) => {
    setSessionFields((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [key]: value } : row))
    );
  };
  return (
    <Box>
      <Grid2 container className="pb-32">
        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
          <Box className="pb-32">
            <CustomTypography id="consult-fields-title" variant="h5">
              Patient Information
            </CustomTypography>
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext
              items={sessionFields.map((r) => r.id)}
              strategy={verticalListSortingStrategy}
            >
              <CustomTable id="session-fields-table">
                <CustomTableHead id="session-fields-table-head">
                  <CustomTableRow id="session-fields-table-header-row">
                    <CustomTableCell
                      id="session-fields-sorting-column"
                      className="width-5"
                    ></CustomTableCell>
                    <CustomTableCell id="session-field-name-column">
                      FIELD NAME
                    </CustomTableCell>
                    <CustomTableCell id="session-field-label-column">
                      FIELD LABEL
                    </CustomTableCell>
                    <CustomTableCell id="session-field-label-column">
                      FIELD VALUE
                    </CustomTableCell>
                  </CustomTableRow>
                </CustomTableHead>
                <CustomTableBody id="session-fields-table-body">
                  {sessionFields.map((row, index) => {
                    const rowError = Array.isArray(errors)
                      ? errors[index]
                      : undefined;
                    const rowTouched = Array.isArray(touched)
                      ? touched[index]
                      : undefined;
                    return (
                      <SortableRow
                        key={row.id}
                        row={row}
                        onChange={handleTextChange}
                        errors={rowError}
                        touched={rowTouched}
                      />
                    );
                  })}
                </CustomTableBody>
              </CustomTable>
            </SortableContext>
          </DndContext>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default SessionFieldsComponent;
